import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Button, DatePicker, Input, Modal, Select, Space } from 'antd'
import {
  DownOutlined,
  EditOutlined,
  UpOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import ptBR from 'antd/es/date-picker/locale/pt_BR'
import {
  PortalArtigoModel,
  StatusPortalArtigo,
} from '../../../models/PortalArtigoModel'
import { articleFieldIds, portalEditorSettingsIds } from '../helpers/editor-ids'
import { PillarConfigEditor } from './PillarConfigEditor'
import { PortalEditorField } from './PortalEditorField'
import { PortalImagePathField } from './PortalImagePathField'

const { TextArea } = Input

export type PortalArticleSettingsSection =
  | 'publish'
  | 'article'
  | 'cover'
  | 'thumb'
  | 'seo'
  | 'pillar'

interface PortalArticleSettingsSidebarProps {
  artigo: PortalArtigoModel
  isNew: boolean
  tagsInput: string
  expandedSections: PortalArticleSettingsSection[]
  openGraphModalOpen: boolean
  onChange: (patch: Partial<PortalArtigoModel>) => void
  onTagsInputChange: (value: string) => void
  onExpandedSectionsChange: (sections: PortalArticleSettingsSection[]) => void
  onOpenGraphModalOpenChange: (open: boolean) => void
}

type OpenGraphDraft = Pick<
  PortalArtigoModel,
  'tituloOg' | 'descricaoOg' | 'imagemOgUrl' | 'imagemOgAlt'
>

export function PortalArticleSettingsSidebar({
  artigo,
  isNew,
  tagsInput,
  expandedSections,
  openGraphModalOpen,
  onChange,
  onTagsInputChange,
  onExpandedSectionsChange,
  onOpenGraphModalOpenChange,
}: PortalArticleSettingsSidebarProps) {
  const isPillar = artigo.tipo === 'PILLAR'
  const [openGraphDraft, setOpenGraphDraft] = useState<OpenGraphDraft>(() =>
    createOpenGraphDraft(artigo)
  )

  useEffect(() => {
    if (openGraphModalOpen) setOpenGraphDraft(createOpenGraphDraft(artigo))
  }, [artigo, openGraphModalOpen])

  const toggleSection = (section: PortalArticleSettingsSection) => {
    onExpandedSectionsChange(
      expandedSections.includes(section)
        ? expandedSections.filter((current) => current !== section)
        : [...expandedSections, section]
    )
  }

  return (
    <SettingsStack>
      <SettingsCard
        id={portalEditorSettingsIds.publish}
        title="Publicar"
        expanded={expandedSections.includes('publish')}
        onToggle={() => toggleSection('publish')}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PortalEditorField label="Status">
            <Select
              value={artigo.status}
              style={{ width: '100%' }}
              onChange={(status: StatusPortalArtigo) => onChange({ status })}
              options={[
                { value: 'RASCUNHO', label: 'Rascunho' },
                { value: 'PUBLICADO', label: 'Publicado' },
              ]}
            />
          </PortalEditorField>
          <PortalEditorField label="Data de publicação">
            <DatePicker
              value={artigo.dataPublicacao ? dayjs(artigo.dataPublicacao) : null}
              locale={ptBR}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              onChange={(date) =>
                onChange({
                  dataPublicacao: date ? date.startOf('day').toISOString() : undefined,
                })
              }
            />
          </PortalEditorField>
        </Space>
      </SettingsCard>

      <SettingsCard
        id={portalEditorSettingsIds.article}
        title="Configuração do artigo"
        expanded={expandedSections.includes('article')}
        onToggle={() => toggleSection('article')}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PortalEditorField label="Título do artigo">
            <Input
              id={articleFieldIds.titulo}
              value={artigo.titulo}
              placeholder="Título"
              onChange={(event) => onChange({ titulo: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Slug público">
            <Input
              id={articleFieldIds.slug}
              value={artigo.slug}
              placeholder="slug-do-artigo"
              onChange={(event) => onChange({ slug: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Resumo">
            <TextArea
              id={articleFieldIds.resumo}
              value={artigo.resumo}
              placeholder="Resumo"
              autoSize
              onChange={(event) => onChange({ resumo: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Tipo do artigo">
            {isNew ? (
              <Select
                id={articleFieldIds.tipo}
                value={isPillar ? 'PILLAR' : 'COMUM'}
                style={{ width: '100%' }}
                onChange={(tipo) =>
                  onChange({
                    tipo: tipo === 'PILLAR' ? 'PILLAR' : null,
                    configuracaoPillar:
                      tipo === 'PILLAR' ? artigo.configuracaoPillar || {} : undefined,
                  })
                }
                options={[
                  { value: 'COMUM', label: 'Artigo comum' },
                  { value: 'PILLAR', label: 'Artigo Pillar' },
                ]}
              />
            ) : (
              <Input
                id={articleFieldIds.tipo}
                value={isPillar ? 'Artigo Pillar' : 'Artigo comum'}
                disabled
              />
            )}
          </PortalEditorField>
        </Space>
      </SettingsCard>

      <SettingsCard
        id={portalEditorSettingsIds.cover}
        title="Imagem de capa"
        expanded={expandedSections.includes('cover')}
        onToggle={() => toggleSection('cover')}
      >
        <PortalImagePathField
          value={{
            url: artigo.imagemCapaUrl,
            alt: artigo.imagemCapaAlt,
            width: artigo.imagemCapaLargura,
            height: artigo.imagemCapaAltura,
          }}
          urlInputId={articleFieldIds.imagemCapaUrl}
          altInputId={articleFieldIds.imagemCapaAlt}
          onChange={(image) =>
            onChange({
              imagemCapaUrl: image.url,
              imagemCapaAlt: image.alt,
              imagemCapaLargura: image.width,
              imagemCapaAltura: image.height,
            })
          }
        />
      </SettingsCard>

      <SettingsCard
        id={portalEditorSettingsIds.thumb}
        title="Thumb"
        expanded={expandedSections.includes('thumb')}
        onToggle={() => toggleSection('thumb')}
      >
        <PortalImagePathField
          value={{ url: artigo.imagemThumbUrl, alt: artigo.imagemThumbAlt }}
          urlInputId={articleFieldIds.imagemThumbUrl}
          altInputId={articleFieldIds.imagemThumbAlt}
          urlPlaceholder="/images/portal/thumb.png"
          showDimensions={false}
          onChange={(image) =>
            onChange({ imagemThumbUrl: image.url, imagemThumbAlt: image.alt })
          }
        />
        <FallbackText>Em branco, usa a imagem de capa.</FallbackText>
      </SettingsCard>

      <SettingsCard
        id={portalEditorSettingsIds.seo}
        title="SEO Básico"
        expanded={expandedSections.includes('seo')}
        onToggle={() => toggleSection('seo')}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PortalEditorField label="Título SEO">
            <Input
              value={artigo.tituloSeo}
              placeholder="Título SEO"
              onChange={(event) => onChange({ tituloSeo: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Descrição SEO">
            <TextArea
              value={artigo.descricaoSeo}
              placeholder="Descrição SEO"
              autoSize
              onChange={(event) => onChange({ descricaoSeo: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Categoria">
            <Input
              value={artigo.categoria}
              placeholder="Categoria"
              onChange={(event) => onChange({ categoria: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Tags">
            <Input
              value={tagsInput}
              placeholder="tags, separadas, por vírgula"
              onChange={(event) => onTagsInputChange(event.target.value)}
            />
          </PortalEditorField>
          <Button icon={<EditOutlined />} onClick={() => onOpenGraphModalOpenChange(true)}>
            Editar OG
          </Button>
        </Space>
      </SettingsCard>

      {isPillar && (
        <SettingsCard
          id={portalEditorSettingsIds.pillar}
          title="Configuração Pillar"
          expanded={expandedSections.includes('pillar')}
          onToggle={() => toggleSection('pillar')}
        >
          <PillarConfigEditor
            value={artigo.configuracaoPillar}
            blocks={artigo.conteudo || []}
            onChange={(configuracaoPillar) => onChange({ configuracaoPillar })}
          />
        </SettingsCard>
      )}

      <Modal
        title="Open Graph"
        open={openGraphModalOpen}
        okText="Aplicar"
        cancelText="Cancelar"
        onCancel={() => onOpenGraphModalOpenChange(false)}
        onOk={() => {
          onChange(openGraphDraft)
          onOpenGraphModalOpenChange(false)
        }}
      >
        <ModalFields>
          <PortalEditorField label="Título OG" helper="Em branco, usa o título SEO.">
            <Input
              id={articleFieldIds.tituloOg}
              value={openGraphDraft.tituloOg}
              placeholder={artigo.tituloSeo || artigo.titulo || 'Título Open Graph'}
              onChange={(event) =>
                setOpenGraphDraft((current) => ({ ...current, tituloOg: event.target.value }))
              }
            />
          </PortalEditorField>
          <PortalEditorField label="Descrição OG" helper="Em branco, usa a descrição SEO.">
            <TextArea
              id={articleFieldIds.descricaoOg}
              value={openGraphDraft.descricaoOg}
              placeholder={artigo.descricaoSeo || artigo.resumo || 'Descrição Open Graph'}
              autoSize
              onChange={(event) =>
                setOpenGraphDraft((current) => ({ ...current, descricaoOg: event.target.value }))
              }
            />
          </PortalEditorField>
          <PortalImagePathField
            value={{ url: openGraphDraft.imagemOgUrl, alt: openGraphDraft.imagemOgAlt }}
            urlInputId={articleFieldIds.imagemOgUrl}
            altInputId={articleFieldIds.imagemOgAlt}
            urlPlaceholder="/images/portal/open-graph.png"
            showDimensions={false}
            onChange={(image) =>
              setOpenGraphDraft((current) => ({
                ...current,
                imagemOgUrl: image.url,
                imagemOgAlt: image.alt,
              }))
            }
          />
          <FallbackText>Em branco, usa a imagem de capa em 1200 × 630.</FallbackText>
        </ModalFields>
      </Modal>
    </SettingsStack>
  )
}

function createOpenGraphDraft(artigo: PortalArtigoModel): OpenGraphDraft {
  return {
    tituloOg: artigo.tituloOg || '',
    descricaoOg: artigo.descricaoOg || '',
    imagemOgUrl: artigo.imagemOgUrl || '',
    imagemOgAlt: artigo.imagemOgAlt || '',
  }
}

interface SettingsCardProps {
  id: string
  title: string
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}

function SettingsCard({ id, title, expanded, onToggle, children }: SettingsCardProps) {
  return (
    <SettingsSection id={id}>
      <SettingsHeader type="button" aria-expanded={expanded} onClick={onToggle}>
        <span>{title}</span>
        {expanded ? <UpOutlined /> : <DownOutlined />}
      </SettingsHeader>
      {expanded && <SettingsBody>{children}</SettingsBody>}
    </SettingsSection>
  )
}

const SettingsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SettingsSection = styled.section`
  overflow: hidden;
  border: 1px solid #e1e4ed;
  border-radius: 8px;
  background: #fff;
  color: #122a41;
`

const SettingsHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 0;
  padding: 16px;
  background: transparent;
  color: #122a41;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  svg {
    color: #8fa0b5;
  }
`

const SettingsBody = styled.div`
  padding: 4px 16px 16px;
`

const ModalFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`

const FallbackText = styled.p`
  margin: 8px 0 0;
  color: #8c8c8c;
  font-size: 12px;
`
